import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../keys";
import {
  getDashboardEarningsOverviewAPI,
  getDashboardEarningsTrendAPI,
  getDashboardRevenueAPI,
  getDashboardSummaryAPI,
  getTopCustomVideoBuyersAPI,
  getTopPostBuyersAPI,
  getTopSubscribersAPI,
  getTopTippersAPI,
} from "../../api/dashbordApi";
import { getTokens } from "../../utils/tokens";
import type { IRevenueMonth, ITopEarner } from "../../components/Main/Sidebar/Achievements/achievements";

const DASHBOARD_STALE_MS = 60 * 1000;

export function useCreatorDashboardPageQueries() {
  const hasToken = !!getTokens()?.access_token;

  const monthYear = useMemo(() => {
    const currentDate = new Date();
    const month = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1,
    ).padStart(2, "0")}`;
    const year = currentDate.getFullYear().toString();
    return { month, year };
  }, []);

  const currentYear = useMemo(() => new Date().getFullYear().toString(), []);

  const mainQuery = useQuery({
    queryKey: queryKeys.dashboard.main,
    queryFn: async () => {
      const [
        summary,
        topTippers,
        topSubscribers,
        topCustomVideoBuyers,
        topPostBuyers,
      ] = await Promise.all([
        getDashboardSummaryAPI(),
        getTopTippersAPI(),
        getTopSubscribersAPI(),
        getTopCustomVideoBuyersAPI(),
        getTopPostBuyersAPI(),
      ]);
      return {
        summary,
        topTippers,
        topSubscribers,
        topCustomVideoBuyers,
        topPostBuyers,
      };
    },
    enabled: hasToken,
    staleTime: DASHBOARD_STALE_MS,
  });

  const earningsTrendQuery = useQuery({
    queryKey: queryKeys.dashboard.earningsTrend(monthYear.month, monthYear.year),
    queryFn: async () => {
      const trendData = await getDashboardEarningsTrendAPI({
        month: monthYear.month,
        year: monthYear.year,
      });
      return (trendData as { total: number }[]).map(item => item.total);
    },
    enabled: hasToken,
    staleTime: DASHBOARD_STALE_MS,
  });

  const overviewQuery = useQuery({
    queryKey: queryKeys.dashboard.earningsOverview(monthYear.month),
    queryFn: () =>
      getDashboardEarningsOverviewAPI({
        month: monthYear.month,
        year: monthYear.year,
      }),
    enabled: hasToken,
    staleTime: DASHBOARD_STALE_MS,
  });

  const revenueQuery = useQuery({
    queryKey: queryKeys.dashboard.revenue(currentYear),
    queryFn: async () => {
      const data = await getDashboardRevenueAPI(currentYear);
      return (data as { months?: IRevenueMonth[] })?.months ?? [];
    },
    enabled: hasToken,
    staleTime: DASHBOARD_STALE_MS,
  });

  return {
    summary: mainQuery.data?.summary ?? null,
    topTippers: (mainQuery.data?.topTippers ?? []) as ITopEarner[],
    topSubscribers: (mainQuery.data?.topSubscribers ?? []) as ITopEarner[],
    topCustomVideoBuyers: (mainQuery.data?.topCustomVideoBuyers ?? []) as ITopEarner[],
    topPostBuyers: (mainQuery.data?.topPostBuyers ?? []) as ITopEarner[],
    loading: mainQuery.isPending,
    mainError: mainQuery.isError,

    earningsTrend: earningsTrendQuery.data ?? [],
    earningsTrendLoading: earningsTrendQuery.isPending,

    overview: overviewQuery.data ?? null,
    overviewLoading: overviewQuery.isPending,

    revenueData: revenueQuery.data ?? [],
    revenueLoading: revenueQuery.isPending,
  };
}
